/**
 * @license
 * @preserve
 *
 * KeeeX SAS Confidential
 * https://keeex.me
 * Copyright 2013-2022 KeeeX All Rights Reserved.
 * 
 * These computer program listings and specifications, herein,
 * are and remain the property of KeeeX SAS. The intellectual
 * and technical concepts herein are proprietary to KeeeX SAS
 * and may be covered by EU and foreign patents,
 * patents in process, trade secrets and copyright law.
 * 
 * They shall not be reproduced or copied or used in whole or
 * in part as the basis for manufacture or sale of items unless
 * prior written permission is obtained from KeeeX SAS.
 * 
 * For a license agreement, please contact:
 * <mailto: contact@keeex.net>
 * 
 */
/* eslint-env browser */
import { Algorithms as DigestAlgorithms } from "@keeex/crypto/lib/digest.js";
import { NotImplemented, notImplemented } from "@keeex/crypto/lib/exception.js";
import sha2 from "./sha2.js";
/** Convert Digest Algorithm name to browser-supported names */
export const browserNameFromAlgorithmName = algorithm => {
  const result = {
    [DigestAlgorithms.SHA1]: "SHA-1",
    [DigestAlgorithms.SHA224]: null,
    [DigestAlgorithms.SHA256]: "SHA-256",
    [DigestAlgorithms.SHA512]: "SHA-512",
    [DigestAlgorithms.RIPEMD160]: null,
    [DigestAlgorithms.SHA3_224]: null,
    [DigestAlgorithms.SHA3_256]: null,
    [DigestAlgorithms.SHA3_384]: null,
    [DigestAlgorithms.SHA3_512]: null,
    [DigestAlgorithms.KECCAK224]: null,
    [DigestAlgorithms.KECCAK256]: null,
    [DigestAlgorithms.KECCAK384]: null,
    [DigestAlgorithms.KECCAK512]: null
  }[algorithm] ?? null;
  if (result === null) {
    throw notImplemented();
  }
  return result;
};
const blockToImmediate = digest => data => digest.digest(data);
/** Separate loading ripemd module */
const pureRipemd = async data => {
  const {
    ripemdChunk
  } = await import("@keeex/crypto-provider-purejs/lib/digest/ripemd/ripemd.js");
  return blockToImmediate(await ripemdChunk(DigestAlgorithms.RIPEMD160))(data);
};
/** Separate loading sha224 chunk */
const pureSha224 = async data => {
  const {
    Sha224Chunk: sha224Chunk
  } = await import("@keeex/crypto-provider-purejs/lib/digest/sha2/sha224chunk.js");
  return blockToImmediate(new sha224Chunk())(data);
};
/** Separate loading sha3/keccak chunk */
const pureSha3 = async (algorithm, data) => {
  const {
    sha3Chunk
  } = await import("@keeex/crypto-provider-purejs/lib/digest/sha3.js");
  return blockToImmediate(await sha3Chunk(algorithm))(data);
};
/**
 * Some algorithms have no implementation in WebCrypto
 * We handle them in JS
 */
const getFromPureJs = async (algorithm, data) => {
  switch (algorithm) {
    case DigestAlgorithms.RIPEMD160:
      return pureRipemd(data);
    case DigestAlgorithms.SHA224:
      return pureSha224(data);
    case DigestAlgorithms.SHA3_224:
    case DigestAlgorithms.SHA3_256:
    case DigestAlgorithms.SHA3_384:
    case DigestAlgorithms.SHA3_512:
    case DigestAlgorithms.KECCAK224:
    case DigestAlgorithms.KECCAK256:
    case DigestAlgorithms.KECCAK384:
    case DigestAlgorithms.KECCAK512:
      return pureSha3(algorithm, data);
    default:
      throw notImplemented();
  }
};
const digestImmediate = async (algorithm, data) => {
  try {
    return await getFromPureJs(algorithm, data);
  } catch (e) {
    if (e !== NotImplemented) {
      throw e;
    }
  }
  const browserAlgorithm = browserNameFromAlgorithmName(algorithm);
  return sha2(browserAlgorithm, data);
};
const digestProvider = {
  digestImmediate
};
export default digestProvider;
